import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputLabel, useTheme, useMediaQuery } from "@mui/material";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";

function getMiddlePages(pageIndex, pageCount) {
  if (pageCount <= 2) return [];
  const windowSize = 4;
  const maxStart = Math.max(1, pageCount - 1 - windowSize);
  const start = Math.max(1, Math.min(pageIndex - 1, maxStart));
  const end = Math.min(start + windowSize - 1, pageCount - 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
}

const shouldShowLeftEllipsis = (pageIndex, pageCount) =>
  pageCount > 5 && pageIndex > 3;
const shouldShowRightEllipsis = (pageIndex, pageCount) =>
  pageCount > 5 && pageIndex < pageCount - 4;

const PageButton = ({ active, label, onClick, page, ariaLabel }) => (
  <Box
    component="button"
    type="button"
    onClick={() => onClick(page)}
    aria-label={ariaLabel || `Page ${label}`}
    aria-current={active ? "page" : undefined}
    sx={{
      width: "35px",
      height: "35px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "32px",
      cursor: "pointer",
      backgroundColor: active ? "#403C6F" : "#FFFFFF",
      color: active ? "#FFFFFF" : "#4B4F50",
      fontWeight: 500,
      fontSize: "14px",
      p: 0,
      m: 0,
      border: active ? "1px solid #403C6F" : "1px solid #E8E8E9",
      outline: "none",
      "&:hover": {
        backgroundColor: active ? "#403C6F" : "rgba(64, 60, 111, 0.1)",
      },
      "&:focus-visible": { outline: "2px solid #403C6F", outlineOffset: "2px" },
    }}
  >
    {label}
  </Box>
);

PageButton.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClick: PropTypes.func,
  page: PropTypes.number,
  ariaLabel: PropTypes.string,
};

const Ellipsis = () => (
  <Box
    sx={{
      width: "40px",
      height: "40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#403C6F",
      fontWeight: 600,
      fontSize: "14px",
      cursor: "default",
      userSelect: "none",
    }}
    aria-hidden="true"
  >
    ...
  </Box>
);

const PaginationControls = ({
  pageIndex,
  pageSize: initialPageSize,
  totalRows,
  pageCount,
  canPreviousPage,
  canNextPage,
  onPageChange,
  onPageSizeChange,
  isdisplayNumbers = true,
  label = "members",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);

  useEffect(() => {
    setRowsPerPage(initialPageSize);
  }, [initialPageSize]);

  const pageCountSafe =
    rowsPerPage > 0 ? Math.ceil(totalRows / rowsPerPage) : 0;
  const effectivePageIndex =
    pageCountSafe === 0 ? 0 : Math.min(pageIndex, pageCountSafe - 1);
  let start = totalRows === 0 ? 0 : effectivePageIndex * rowsPerPage + 1;
  let end =
    totalRows === 0
      ? 0
      : Math.min((effectivePageIndex + 1) * rowsPerPage, totalRows);
  if (start > end) {
    start = end;
  }

  const displayLabel =
    totalRows === 1 && label.endsWith("s") ? label.slice(0, -1) : label;

  const middlePages = useMemo(
    () => getMiddlePages(pageIndex, pageCount),
    [pageIndex, pageCount]
  );
  const showLeft = shouldShowLeftEllipsis(pageIndex, pageCount);
  const showRight = shouldShowRightEllipsis(pageIndex, pageCount);

  return (
    <Box
      component="nav"
      aria-label="Pagination"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      sx={{
        boxShadow: "none",
        padding: "8px 0",
      }}
    >
      <Box display="flex" alignItems="center">
        <InputLabel
          htmlFor="rows-per-page-input"
          sx={{
            whiteSpace: "nowrap",
            color: "#22223b",

            fontWeight: 700,
            fontSize: "12px",
            lineHeight: "150%",
            letterSpacing: "0%",
            display: "inline-block",
            transform: "rotate(0deg)",
            mb: 0,
          }}
        >
          Rows per page:
        </InputLabel>
        <TextField
          id="rows-per-page-input"
          inputMode="numeric"
          type="text"
          value={rowsPerPage}
          onChange={(e) => {
            const value = e.target.value.replaceAll(/\D/g, "");
            setRowsPerPage(value);
          }}
          onBlur={() => {
            let value = Number.parseInt(rowsPerPage, 10) || 30;
            if (value < 1) value = 1;
            if (value > 30) value = 30;
            setRowsPerPage(value);
            onPageSizeChange(value);
          }}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          slotProps={{
            htmlInput: {
              style: {
                width: "20px",
                height: "18px",
                padding: "2px 4px",
                margin: 0,
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "150%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#403C6F",
                backgroundColor: "#FFFFFF",
              },
              autoComplete: "off",
              "aria-label": "Rows per page",
            },
          }}
          variant="outlined"
          size="small"
          sx={{
            width: "26px",
            height: "28px",
            marginLeft: "6px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E8E8E9",
            borderRadius: "4px",
            "& .MuiOutlinedInput-root": {
              height: "30px",
              "& fieldset": { border: "none" },
              "&:hover fieldset": { border: "none" },
              "&.Mui-focused fieldset": { border: "none" },
            },
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
            {
              "-webkit-appearance": "none",
              margin: 0,
            },
          }}
        />
      </Box>

      <Typography
        variant="body2"
        sx={{
          height: "24px",
          color: "#22223b",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "150%",
          letterSpacing: "0%",
          display: "flex",
          alignItems: "center",
        }}
        aria-live="polite"
      >
        {!isMobile &&
          isdisplayNumbers &&
          `Showing ${start}-${end} of ${totalRows} ${displayLabel}`}
      </Typography>

      <Box display="flex" alignItems="center" gap="10px">
        <Box
          component="button"
          type="button"
          onClick={() => canPreviousPage && onPageChange(pageIndex - 1)}
          disabled={!canPreviousPage}
          aria-label="Previous page"
          sx={{
            width: "35px",
            height: "35px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "32px",
            cursor: canPreviousPage ? "pointer" : "not-allowed",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E8E8E9",
            p: 0,
            m: 0,
            opacity: canPreviousPage ? 1 : 0.5,
            "&:hover": {
              backgroundColor: canPreviousPage
                ? "rgba(232, 232, 233, 0.5)"
                : "#FFFFFF",
            },
            "&:disabled": {
              opacity: 0.5,
              cursor: "not-allowed",
            },
          }}
        >
          <NavigateBefore
            sx={{
              color: "#1B2F3F",
              width: "16px",
              height: "16px",
              "& path": { strokeWidth: "2px" },
            }}
          />
        </Box>

        <PageButton
          active={pageIndex === 0}
          label={1}
          page={0}
          onClick={onPageChange}
          ariaLabel="Page 1"
        />

        {showLeft && <Ellipsis />}

        {middlePages.map((i) => (
          <PageButton
            key={`page-${i}`}
            active={pageIndex === i}
            label={i + 1}
            page={i}
            onClick={onPageChange}
            ariaLabel={`Page ${i + 1}`}
          />
        ))}

        {showRight && <Ellipsis />}

        {pageCount > 1 && (
          <PageButton
            active={pageIndex === pageCount - 1}
            label={pageCount}
            page={pageCount - 1}
            onClick={onPageChange}
            ariaLabel={`Page ${pageCount}`}
          />
        )}

        <Box
          component="button"
          type="button"
          onClick={() => canNextPage && onPageChange(pageIndex + 1)}
          disabled={!canNextPage}
          aria-label="Next page"
          aria-disabled={!canNextPage}
          sx={{
            width: "35px",
            height: "35px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "32px",
            cursor: canNextPage ? "pointer" : "not-allowed",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E8E8E9",
            p: 0,
            m: 0,
            opacity: canNextPage ? 1 : 0.5,
            "&:hover": {
              backgroundColor: canNextPage
                ? "rgba(64, 60, 111, 0.1)"
                : "#FFFFFF",
            },
            "&:disabled": {
              opacity: 0.5,
              cursor: "not-allowed",
            },
          }}
        >
          <NavigateNext
            sx={{
              color: "#1B2F3F",
              width: "16px",
              height: "16px",
              "& path": {
                strokeWidth: "2px",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PaginationControls;

PaginationControls.propTypes = {
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  totalRows: PropTypes.number,
  pageCount: PropTypes.number,
  canPreviousPage: PropTypes.bool,
  canNextPage: PropTypes.bool,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  isdisplayNumbers: PropTypes.bool,
  label: PropTypes.string,
};
